import { useState, useEffect } from "react";
import { SortableContainer, SortableContainerProps, SortableElement, SortableElementProps } from "react-sortable-hoc";
import { arrayMoveImmutable } from "array-move";
import { intl } from "LocaleWrapper";

import { Button } from "@cofano/cck";
import { useTheme } from "@mui/material";

import { CustomDialog } from "components/dialogs/Dialogs";

import { Language } from "utils";

import { AgreementFormInput } from "view/agreements/validation/agreement.zod";

type UpdateAgreementOrderDialogProps = {
	open: boolean;
	onClose: () => void;
	agreements: AgreementFormInput[];
	refetch: (agreements: AgreementFormInput[]) => void;
};

const UpdateAgreementOrderDialog = ({ open, onClose, agreements, refetch }: UpdateAgreementOrderDialogProps) => {

	const [ list, setList ] = useState<AgreementFormInput[]>([]);

	const theme = useTheme();

	const handleOnClose = () => {
		onClose();
		setList([]);
	};

	const save = () => {
		handleOnClose();
		refetch(list);
	};

	const SortableItem: React.ComponentClass<SortableElementProps & { item: AgreementFormInput }> = (
		SortableElement(({ item }: { item: AgreementFormInput }) => (
			<div
				className="sortable-item"
				style={{
					backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#FFF",
					borderBottom: theme.palette.mode === "dark" ? "1px solid rgba(0, 0, 0, 0.87)" : "1px solid #F0F0F0",
					color: theme.palette.mode === "dark" ? "#fff" : "#333",
				}}>
				<div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
					{item.summary || item.description || item.type}
				</div>
			</div>
		))
	);

	const SortableList: React.ComponentClass<SortableContainerProps & { items: AgreementFormInput[] }> = (
		SortableContainer(({ items }: { items: AgreementFormInput[] }) => (
			<div
				className="sortable-list"
				style={{
					backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#F3F3F3",
					border: theme.palette.mode === "dark" ? "1px solid rgba(0, 0, 0, 0.87)" : "1px solid #E5E5E5",
				}}>
				{items.map((item: AgreementFormInput, index: number) => <SortableItem index={index} item={item} />)}
			</div>
		))
	);

	const onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number, newIndex: number }) => {
		setList(arrayMoveImmutable([ ...list ], oldIndex, newIndex));
	};

	useEffect(() => {
		if (open) {
			setList(agreements);
		}
	}, [ open ]);

	return (
		<CustomDialog
			open={open}
			title={Language("CHANGE_ORDER")}
			onClose={() => handleOnClose()}
			maxWidth="sm"
			dialogStyling={{ content: { backgroundColor: theme.palette.mode === "dark" ? "inherit" : "#EEE" } }}
			actions={(
				<Button onClick={() => save()} size="small">
					{intl.formatMessage({ id: "SAVE" })}
				</Button>
			)}>
			<div className="change-status-order-dialog">
				<SortableList
					items={list}
					onSortEnd={onSortEnd}
					helperClass={"sortable-status-helper"} />
			</div>
		</CustomDialog>
	);
};

export default UpdateAgreementOrderDialog;
